import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useApi } from "@/lib/useApi";
import { useT, Bi } from "@/lib/i18n";
import { Card, ErrorState, ListSkeleton, PageTitle } from "@/components/common";
import { Field, BigInput } from "@/components/form";
import { BackLink } from "@/pages/More";

const DAYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const MEALS = ["breakfast", "lunch", "dinner"] as const;
const todayKey = DAYS[(new Date().getDay() + 6) % 7];

export default function MenuPage() {
  const { t, lang } = useT();
  const { property } = useAuth();
  const { data, loading, error, reload } = useApi<any>(property ? `/properties/${property.id}/menu` : null);
  const [week, setWeek] = useState<any>(null);
  const [day, setDay] = useState(todayKey);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (data) setWeek(data.week); }, [data]);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/properties/${property.id}/menu`, { week });
      toast.success(t("menu_saved"));
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };
  const dayName = (d: string) => new Date(2024, 0, 1 + DAYS.indexOf(d)).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { weekday: "short" });
  return (
    <div>
      <BackLink />
      <PageTitle title={t("food_menu")} sub={property?.name} />
      {loading && !week ? <ListSkeleton rows={3} /> : error ? <ErrorState message={error} onRetry={reload} /> : week && (
        <>
          <div className="grid grid-cols-7 gap-1.5 mb-4" data-testid="menu-day-tabs">
            {DAYS.map((d) => (
              <button key={d} data-testid={`menu-day-${d}`} onClick={() => setDay(d)}
                className={`h-12 rounded-xl text-xs font-bold pg-tap relative ${day === d ? "bg-emerald-700 text-white" : "bg-white border border-slate-200 text-slate-600"}`}>
                {dayName(d)}
                {d === todayKey && <span className="absolute top-1 right-1 h-1.5 w-1.5 rounded-full bg-amber-400" />}
              </button>
            ))}
          </div>
          <Card className="p-4 space-y-4" key={day}>
            {day === todayKey && <p className="text-xs font-semibold text-amber-700">{t("today")}</p>}
            {MEALS.map((m) => (
              <Field key={m} label={<Bi k={m} />}>
                <BigInput data-testid={`menu-${day}-${m}-input`} value={week[day][m]} maxLength={120}
                  onChange={(e) => setWeek({ ...week, [day]: { ...week[day], [m]: e.target.value } })} />
              </Field>
            ))}
          </Card>
          <Button data-testid="menu-save-btn" disabled={busy} onClick={save} className="mt-4 w-full h-14 rounded-xl text-base bg-emerald-700 hover:bg-emerald-800">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : t("save")}
          </Button>
        </>
      )}
    </div>
  );
}
